import React, { useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Container,
  Card,
  useTheme,
  ThemeProvider,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppTheme } from "@/utils/theme";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const AccessDeniedPage: React.FC = () => {
  const theme = useAppTheme();
  const clientTheme = useTheme();
  const { logout } = useAuth0();
  const auditLogCalledRef = React.useRef(false);
  const createAuditLog = useCreateAuditlog();

  const signOutFunction = async () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  useEffect(() => {
    if (!auditLogCalledRef.current) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.AUTHORIZATION,
        option: OPTIONS.INACTIVE_ACCOUNT,
        actionCode: ACTION_CODE.WEB_INVALID_ACCOUNT,
        action: "Inactive or not existing account",
        detail: "An inactive account or a not existing one",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: `${process.env.REACT_APP_ACCOUNT_ID}`,
        name: "system",
      });
      auditLogCalledRef.current = true;
    }
  }, [createAuditLog]);

  return (
    <ThemeProvider theme={theme}>
      <Container
        sx={{
          maxWidth: "100%",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          padding: 0,
          backgroundColor: theme.palette.background?.default || "#f5f5f5",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "15px",
            left: "50%",
            transform: "translateX(-50%)",
            width: { xs: 60, sm: 79 },
            height: { xs: 60, sm: 78 },
          }}
        >
          <img
            src="/assets/logo@2x.png"
            alt="Logo"
            style={{ width: "100%", height: "auto" }}
          />
        </Box>

        <Card
          sx={{
            width: "90%",
            maxWidth: 480,
            padding: { xs: "40px 20px", sm: "60px 40px" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
            borderRadius: clientTheme.shape.borderRadius,
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            border: "1px solid #ddd",
            textAlign: "center",
            backgroundColor: theme.palette.surface?.light || "#ffffff",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              marginBottom: 1,
              fontFamily: clientTheme.typography.fontFamily,
              fontSize: { xs: "24px", sm: clientTheme.typography.h4.fontSize },
              fontWeight: clientTheme.typography.h4.fontWeight || 700,
              lineHeight: {
                xs: "32px",
                sm: clientTheme.typography.h4.lineHeight,
              },
              color: theme.palette.textMain?.main || "#000000",
            }}
          >
            Access Denied
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: theme.palette.textMain?.dark2 || "#666666",
              fontFamily: clientTheme.typography.fontFamily,
              fontSize: {
                xs: "14px",
                sm: clientTheme.typography.body1.fontSize,
              },
              mb: 2,
            }}
          >
            You do not have access to COYN. Please contact your system
            administrator.
          </Typography>

          <Button
            variant="contained"
            onClick={signOutFunction}
            fullWidth
            sx={{
              borderRadius: "8px",
              height: "48px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.textMain?.light || "#ffffff",
              textTransform: "uppercase",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Back to Sign In
          </Button>
        </Card>
      </Container>
    </ThemeProvider>
  );
};

export default AccessDeniedPage;
